import * as React from 'react';
import classnames from 'classnames';
import { camelCase, flatten, get, isArray, isEmpty, isNil, snakeCase } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Popover from 'components/popover/Popover';
import Tooltip from 'components/Tooltip';

import OverdueDeliverablesPopover from 'modules/organizations/components/overdue-deliverables/popovers/OverdueDeliverablesPopover';

import { DELIVERABLE_ICONS, DELIVERABLE_TYPES } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate, getDisplayDateRange } from 'utils/date';

import './deliverable-type-counters.scss';

import type {
  Deliverable,
  DeliverableType,
  DeliverablesBySubcampaignId,
  DeliverablesOverdueBySubcampaignIdLocal,
  DeliverablesOverdueLocal,
  DeliverablesOverview,
  SubcampaignOfferDeliverablesOverview
} from 'types';

type Props = {
  subcampaignId?: number,
  deliverables?: Array<Partial<Deliverable>>,
  deliverablesCount?: DeliverablesOverview,
  deliverablesOverview?: SubcampaignOfferDeliverablesOverview,
  overdueDeliverables?: DeliverablesOverdueBySubcampaignIdLocal | undefined,
  subcampaignOfferSortState?: string
};

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./deliverable-type-counters.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'deliverable_type_counters';

class DeliverableTypeCounters extends React.PureComponent<Props> {
  private hasOverdueDeliverables(): boolean {
    const { subcampaignId, overdueDeliverables } = this.props;

    if (isNil(overdueDeliverables)
      || isEmpty(overdueDeliverables)
      || !subcampaignId
      || isNil(overdueDeliverables[subcampaignId])
    ) {
      return false;
    }

    return true;
  }

  private getOverdueDeliverables(): DeliverablesOverdueLocal | null {
    const { subcampaignId, overdueDeliverables } = this.props;

    if (!this.hasOverdueDeliverables() || isNil(overdueDeliverables) || !subcampaignId) {
      return null;
    }

    return overdueDeliverables[subcampaignId];
  }

  private getDeliverablesBySubcampaignId(): DeliverablesBySubcampaignId {
    const { deliverablesOverview } = this.props;
    const deliverablesBySubcampaignId = {};

    if (isNil(deliverablesOverview)) {
      return {};
    }

    DELIVERABLE_TYPES.forEach(type => {
      const deliverableGroup = deliverablesOverview[camelCase(type)] || [];

      if (!isEmpty(deliverableGroup)) {
        const subcampaignId: any = get(deliverableGroup, '[0].subcampaignId');
        let groupBySubcampaignId = deliverablesBySubcampaignId[subcampaignId];

        groupBySubcampaignId = isArray(groupBySubcampaignId) ? groupBySubcampaignId : [];
        groupBySubcampaignId.push(deliverableGroup);
        groupBySubcampaignId = flatten(groupBySubcampaignId);

        deliverablesBySubcampaignId[subcampaignId] = groupBySubcampaignId;
      }
    });

    return deliverablesBySubcampaignId;
  }

  renderCount(type: DeliverableType, count: number) {
    return (
      <div
        title={__t(`${COMMON_MSG_KEY}.types.${type}.singular`)}
        className="deliverable-type-counters__type"
      >
        <div className="deliverable-type-counters__icon">
          <SVGDynamicIcon
            name={`${DELIVERABLE_ICONS[camelCase(type)]}`}
            width={16}
            height={16}
          />
        </div>
        <div className="deliverable-type-counters__count">
          {count}
        </div>
      </div>
    );
  }

  renderTooltip(type: DeliverableType, count: number): React.ReactNode {
    return (
      <div
        key={`deliverable-type-counters--${type}-${count}`}
        className="deliverable-type-counters__tooltip"
      >
        <Tooltip
          message={__t(`${COMMON_MSG_KEY}.types.${type}.singular`)}
          width="short"
          classNameWrapper="deliverable-type-counters__tooltip-wrapper"
        >
          {this.renderCount(type, count)}
        </Tooltip>
      </div>
    );
  }

  renderSingleTimeline(deliverable: Partial<Deliverable>) {
    return (
      <div className="deliverable-type-counters__popover-date">
        {getDisplayDate(deliverable.startDate)} - {getDisplayDate(deliverable.endDate)}
      </div>
    );
  }

  renderMultiTimeline(timelines: Array<string>, deliverable: Partial<Deliverable>) {
    return (
      <div className="deliverable-type-counters__popover-timeline-container">
        {
          timelines.map(
            timeline => (
              <div className="deliverable-type-counters__popover-timeline" key={timeline}>
                <div>
                  {__t(`${MSG_KEY}.timeline.${snakeCase(timeline)}`)}
                </div>
                <div>
                  {getDisplayDateRange(deliverable[timeline].startDate, deliverable[timeline].endDate)}
                </div>
              </div>
            )
          )
        }
      </div>
    );
  }

  renderStandardPopover(deliverables: Array<Partial<Deliverable>>,
    type: DeliverableType,
    count: number
  ): React.ReactNode {
    return (
      <Popover
        key={`deliverable-type-counters--${JSON.stringify(deliverables)}`}
        triggerComponent={this.renderCount(type, count)}
        classNameWrapper="deliverable-type-counters__popover-wrapper"
        classNamePopover="deliverable-type-counters__popover"
      >
        {
          deliverables.map((deliverable, index) => {
            const timelines = ['contentApprovalTimeline', 'contentGoLiveTimeline', 'insightsProvisionTimeline']
              .filter(timeline => deliverable[timeline]?.startDate);

            return (
              <React.Fragment key={`deliverable-type-counters--${deliverable.name}-${index}`}>
                <div className="deliverable-type-counters__popover-title">
                  {deliverable.name}
                </div>
                {
                  !timelines.length ? this.renderSingleTimeline(deliverable) : this.renderMultiTimeline(timelines, deliverable)
                }
              </React.Fragment>
            );
          })
        }
      </Popover>
    );
  }

  renderOverduePopover(
    deliverables: Array<Partial<Deliverable>>,
    type: DeliverableType,
    count: number
  ): React.ReactNode {
    const { subcampaignId, overdueDeliverables, subcampaignOfferSortState } = this.props;

    if (isNil(overdueDeliverables) || !subcampaignId) {
      return null;
    }

    const { listById } = overdueDeliverables[subcampaignId];
    const isV2 = deliverables.some(deliverable => deliverable.contentApprovalV2);

    if (isEmpty(listById) || !isV2) {
      return this.renderStandardPopover(deliverables, type, count);
    }

    const wrapperClasses = classnames('deliverable-type-counters__popover-wrapper', {
      'deliverable-type-counters__popover-wrapper--no-overdue': !this.hasOverdueDeliverables(),
      'deliverable-type-counters__popover-wrapper--is-v2': isV2
    });

    const popoverClasses = classnames('deliverable-type-counters__popover', {
      'deliverable-type-counters__popover--no-overdue': !this.hasOverdueDeliverables(),
      'deliverable-type-counters__popover--is-v2': isV2
    });

    const deliverablesBySubcampaignId = this.getDeliverablesBySubcampaignId();
    const updatedDeliverables = deliverablesBySubcampaignId[subcampaignId];

    return (
      <OverdueDeliverablesPopover
        key={`deliverable-type-counters--${JSON.stringify(deliverables)}`}
        overdueDeliverables={this.getOverdueDeliverables()}
        deliverables={updatedDeliverables}
        deliverableType={type}
        subcampaignOfferSortState={subcampaignOfferSortState}
        triggerComponent={this.renderCount(type, count)}
        classNameWrapper={wrapperClasses}
        classNamePopover={popoverClasses}
      />
    );
  }

  render() {
    const { deliverables, deliverablesCount, deliverablesOverview } = this.props;

    return (
      <div className="deliverable-type-counters">
        {
          deliverables && DELIVERABLE_TYPES.map(type => {
            const deliverablesMatch = (deliverables || []).filter(d => d.deliverableType === type);

            return !isEmpty(deliverablesMatch)
              ? this.renderTooltip(type as DeliverableType, deliverablesMatch.length)
              : null;
          })
        }
        {
          deliverablesCount && DELIVERABLE_TYPES.map(type => {
            const count = deliverablesCount[camelCase(type)];

            return count && count > 0
              ? this.renderTooltip(type as DeliverableType, count)
              : null;
          })
        }
        {
          deliverablesOverview && DELIVERABLE_TYPES.map(type => {
            const deliverableGroup = deliverablesOverview[camelCase(type)] || [];
            const count = deliverableGroup.length;

            if (count > 0 && !isEmpty(deliverableGroup)) {
              return this.hasOverdueDeliverables()
                ? this.renderOverduePopover(deliverableGroup, type, count)
                : this.renderStandardPopover(deliverableGroup, type, count);
            }

            return null;
          })
        }
      </div>
    );
  }
}

export default DeliverableTypeCounters;
